/* Directory group -> tenant role rules (PB-136 / PPL-04).
   Separate from admin-users-api because this surface is about RULES and the
   proposals they imply, not about administering a person directly. Applying a
   proposal deliberately does NOT reuse `updateTenantRole`: the dedicated
   endpoint re-derives the proposal server-side and stamps the resulting grant as
   mapping-sourced, which is what later makes a safe revocation possible. */

import { AdminApiError, type TenantRole } from "./admin-users-api.js";
import { csrfHeaders } from "../auth/session-api.js";

const BASE = "/api/auth/admin/directory-group-role-mappings";

export type GroupRoleMappingStatus = "active" | "retired";

/** `directory_groups_status_check` (migration 202608040001). */
export type DirectoryGroupStatus = "active" | "archived";
/** `directory_groups_source_check` (migration 202608040001). */
export type DirectoryGroupSource = "admin" | "microsoft_entra" | "scim";

export interface GroupRoleMapping {
  mappingId: string;
  groupId: string;
  groupDisplayName: string;
  groupStatus: DirectoryGroupStatus;
  groupSource: DirectoryGroupSource;
  role: TenantRole;
  status: GroupRoleMappingStatus;
  memberCount: number;
  createdAt: string;
  createdByUserId: string;
  retiredAt: string | null;
}

export interface GroupRoleMappingPage {
  mappings: GroupRoleMapping[];
  nextCursor: string | null;
}

export type PendingRoleChangeBlock =
  | "last-admin"
  | "stale-group-data"
  | "membership-not-active";

export type PendingRoleChangeReason =
  | "in-mapped-group"
  | "left-mapped-group"
  | "rule-retired";

/** The two ADDITIVE roles a group rule can imply (ADR-0078). */
export type MappableRole = Extract<
  TenantRole,
  "tenant-admin" | "transformation-lead"
>;

export interface PendingRoleChange {
  kind: "grant" | "revoke";
  userId: string;
  displayName: string | null;
  email: string | null;
  /**
   * WHICH additive role this proposal grants or revokes. A person can hold one
   * proposal per role, so a consumer applying a row sends it back — the two
   * cannot be confused server-side either way, but naming it keeps the row's
   * copy honest ("Make Transformation Lead", not "Make Administrator").
   */
  role: MappableRole;
  currentRole: TenantRole;
  targetRole: TenantRole;
  reason: PendingRoleChangeReason;
  groupId: string | null;
  groupDisplayName: string | null;
  blockedBy: PendingRoleChangeBlock | null;
}

export interface GroupDataHealth {
  groupDataTrusted: boolean;
  reason: string | null;
}

export interface PendingRoleChangePage {
  changes: PendingRoleChange[];
  matchedCount: number;
  pendingGrantCount: number;
  pendingRevokeCount: number;
  blockedCount: number;
  groupData: GroupDataHealth;
  truncated: boolean;
}

/* Tenant-scoped, like the sibling admin pages' keys (PB-1556): an in-place
   tenant handover keeps a route mounted, so an unscoped key would serve the
   previous tenant's cached rules, proposals and group NAMES to the next
   administrator until revalidation. The rules prefix stays first so one
   invalidate still refreshes all three.

   They live beside the fetchers, as `tenantConfigurationQueryKey` and
   `directoryStatusQueryKey` do, because that prefix relationship is the whole
   reason `invalidateQueries` on the rules key is enough — a reader who has only
   one of the three in front of them cannot see the invariant they must preserve
   (PB-4827: Readiness reads the pending key too). */
export const groupRoleMappingsQueryKey = (tenantId: string | null) =>
  ["auth", "admin", "group-role-mappings", tenantId] as const;
export const pendingRoleChangesQueryKey = (tenantId: string | null) =>
  ["auth", "admin", "group-role-mappings", tenantId, "pending"] as const;
export const assignableGroupsQueryKey = (
  tenantId: string | null,
  query: string,
) =>
  [
    "auth",
    "admin",
    "group-role-mappings",
    tenantId,
    "assignable-groups",
    query,
  ] as const;

export async function listGroupRoleMappings(
  status?: GroupRoleMappingStatus,
  cursor?: string | null,
): Promise<GroupRoleMappingPage> {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  if (cursor) params.set("cursor", cursor);
  const query = params.toString();
  return (await request(
    query ? `${BASE}?${query}` : BASE,
  )) as GroupRoleMappingPage;
}

export interface AssignableGroup {
  groupId: string;
  displayName: string;
  source: DirectoryGroupSource;
  memberCount: number;
  /** The role this group's active rule implies; null when it has no rule. */
  mappedRole: TenantRole | null;
}

export interface AssignableGroupPage {
  groups: AssignableGroup[];
  nextCursor: string | null;
}

/** The groups an admin can attach a rule to — the picker's data source. */
export async function listAssignableGroups(
  options: { query?: string; cursor?: string | null } = {},
): Promise<AssignableGroupPage> {
  const params = new URLSearchParams();
  if (options.query) params.set("query", options.query);
  if (options.cursor) params.set("cursor", options.cursor);
  const query = params.toString();
  return (await request(
    query ? `${BASE}/assignable-groups?${query}` : `${BASE}/assignable-groups`,
  )) as AssignableGroupPage;
}

export async function listPendingRoleChanges(): Promise<PendingRoleChangePage> {
  return (await request(`${BASE}/pending`)) as PendingRoleChangePage;
}

export async function setGroupRoleMapping(
  groupId: string,
  role: MappableRole,
): Promise<GroupRoleMapping> {
  return (await request(`${BASE}/${encodeURIComponent(groupId)}`, {
    method: "PUT",
    body: JSON.stringify({ role }),
  })) as GroupRoleMapping;
}

export async function retireGroupRoleMapping(groupId: string): Promise<void> {
  await request(`${BASE}/${encodeURIComponent(groupId)}`, { method: "DELETE" });
}

/**
 * What a `transformation-lead` REVOCATION additionally carries (ADR-0078): it
 * runs through the same staged, idempotent removal as the Members page — the
 * person's LAMA ownership is transferred to `replacementUserId` BEFORE the role
 * goes — so the caller names the replacement (or `null` when they own nothing)
 * and a client-chosen key that makes a retry resume rather than repeat.
 */
export interface ApplyRoleChangeOptions {
  readonly replacementUserId?: string | null;
  readonly idempotencyKey?: string;
}

/**
 * Apply one proposal. The target role comes from the server's derivation;
 * `role` only names WHICH of the person's proposals, since one person can hold
 * one per additive role.
 */
export async function applyPendingRoleChange(
  userId: string,
  kind: "grant" | "revoke",
  role: MappableRole,
  options: ApplyRoleChangeOptions = {},
): Promise<void> {
  await request(`${BASE}/pending/apply`, {
    method: "POST",
    body: JSON.stringify({
      userId,
      kind,
      role,
      ...(options.replacementUserId !== undefined
        ? { replacementUserId: options.replacementUserId }
        : {}),
      ...(options.idempotencyKey !== undefined
        ? { idempotencyKey: options.idempotencyKey }
        : {}),
    }),
  });
}

async function request(path: string, init: RequestInit = {}): Promise<unknown> {
  const response = await fetch(path, {
    ...init,
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...csrfHeaders(),
      ...init.headers,
    },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      code?: string;
      message?: string;
    } | null;
    throw new AdminApiError(
      body?.message ?? body?.code ?? `Request failed (${response.status})`,
      body?.code,
    );
  }
  return response.status === 204 ? null : response.json();
}
